export interface ProgrammingStateTransition {
  displayState: string;
  targetState: string | null;
  operationsNextStates: string[];
  salesNextStates: string[];
  automaticTransitions: ProgrammingAutomaticStateTransition[];
  owner: 'OPERATIONS' | 'SALES' | 'NONE';
  reason: string;
}

export interface ProgrammingAutomaticStateTransition {
  fromState: string;
  toState: string;
  description: string;
}

const normalizeState = (state: string | null | undefined): string =>
  (state ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toUpperCase();

// Solo estados visibles en Operations/Programación. Estados como BORRADOR,
// COTIZADO, NO APROBADO y APROBADO pertenecen al flujo visible de Sales.
export const OPERATIONS_NEXT_STATES_BY_DISPLAY_STATE: Record<string, string[]> = {
  'REVISAR': [],
  'LAST MINUTE': ['PROGRAMADO'],
  'NUEVO PEDIDO': ['EN PROCESO'],
  'EN PROCESO': ['CONFIRMADO'],
  'CONFIRMADO': [],
  'RECONFIRMADO': ['PROGRAMADO'],
  'PROGRAMADO': [],
  'EN OPERACION': [],
  'OPERADO': ['CERRADO'],
  'CERRADO': [],
  'ANULADO': [],
  'POSTERGADO': []
};

export const SALES_NEXT_STATES_BY_DISPLAY_STATE: Record<string, string[]> = {
  'BORRADOR': [],
  'COTIZADO': [],
  'NO APROBADO': [],
  'APROBADO': ['ANULADO'],
  'REVISAR': ['ANULADO'],
  'LAST MINUTE': ['ANULADO'],
  'NUEVO PEDIDO': ['ANULADO', 'POSTERGADO'],
  'EN PROCESO': ['ANULADO'],
  'CONFIRMADO': ['RECONFIRMADO', 'ANULADO'],
  'RECONFIRMADO': ['ANULADO'],
  'PROGRAMADO': ['ANULADO'],
  'EN OPERACION': [],
  'OPERADO': ['CERRADO'],
  'CERRADO': ['NUEVO PEDIDO'],
  'ANULADO': [],
  'POSTERGADO': ['NUEVO PEDIDO', 'ANULADO']
};

// Estados administrados por el sistema: no deben exponerse como acciones manuales.
// PROGRAMADO -> EN OPERACION se ejecuta automáticamente el mismo día de operación.
// EN OPERACION -> OPERADO se ejecuta automáticamente al último día de operación.
export const AUTOMATIC_NEXT_STATES_BY_DISPLAY_STATE: Record<
  string,
  ProgrammingAutomaticStateTransition[]
> = {
  'BORRADOR': [],
  'COTIZADO': [],
  'NO APROBADO': [],
  'APROBADO': [],
  'REVISAR': [],
  'LAST MINUTE': [],
  'NUEVO PEDIDO': [],
  'EN PROCESO': [],
  'CONFIRMADO': [],
  'RECONFIRMADO': [],
  'PROGRAMADO': [
    {
      fromState: 'PROGRAMADO',
      toState: 'EN OPERACION',
      description: 'El sistema cambia el file automáticamente el mismo día de operación.'
    }
  ],
  'EN OPERACION': [
    {
      fromState: 'EN OPERACION',
      toState: 'OPERADO',
      description: 'El sistema cambia el file automáticamente al último día de operación.'
    }
  ],
  'OPERADO': [],
  'CERRADO': [],
  'ANULADO': [],
  'POSTERGADO': []
};

const getRelevantAutomaticTransitions = (
  displayState: string,
  targetState: string | null
): ProgrammingAutomaticStateTransition[] =>
  [
    ...(AUTOMATIC_NEXT_STATES_BY_DISPLAY_STATE[displayState] ?? []),
    ...(targetState ? AUTOMATIC_NEXT_STATES_BY_DISPLAY_STATE[targetState] ?? [] : [])
  ];

export const getOperationsProgrammingStateTransition = (
  displayState: string | null | undefined
): ProgrammingStateTransition => {
  const normalizedState = normalizeState(displayState);
  const operationsNextStates = OPERATIONS_NEXT_STATES_BY_DISPLAY_STATE[normalizedState] ?? [];
  const targetState = operationsNextStates[0] ?? null;
  const salesNextStates = SALES_NEXT_STATES_BY_DISPLAY_STATE[normalizedState] ?? [];
  const automaticTransitions = getRelevantAutomaticTransitions(
    normalizedState,
    targetState
  );

  if (targetState) {
    return {
      displayState: normalizedState,
      targetState,
      operationsNextStates,
      salesNextStates,
      automaticTransitions,
      owner: 'OPERATIONS',
      reason: 'Operations puede ejecutar el siguiente cambio de estado.'
    };
  }

  if (salesNextStates.length > 0) {
    return {
      displayState: normalizedState,
      targetState: null,
      operationsNextStates,
      salesNextStates,
      automaticTransitions,
      owner: 'SALES',
      reason: 'El siguiente cambio de este estado corresponde al flujo de Sales.'
    };
  }

  return {
    displayState: normalizedState || 'SIN ESTADO',
    targetState: null,
    operationsNextStates,
    salesNextStates,
    automaticTransitions,
    owner: 'NONE',
    reason: 'Este estado no tiene siguiente estado disponible en Programación de Operations.'
  };
};
